/* ============================================================================
   ASTROLOOT — Cosmos canvas
   Layered starfield with parallax drift, occasional shooting stars, and a
   soft particle dust layer. Paints once and stops when reduced motion is on,
   pauses entirely when scrolled out of view or the tab is hidden.
   Usage: <canvas data-cosmos data-density="1" data-shooting="true"></canvas>
   ========================================================================= */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
                document.documentElement.classList.contains('motion-off');

  function hexToRgb(hex) {
    var h = (hex || '').replace('#', '').trim();
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    var n = parseInt(h, 16);
    if (isNaN(n)) return [255, 255, 255];
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }

  function Cosmos(canvas) {
    var ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    var density = parseFloat(canvas.getAttribute('data-density') || '1');
    var allowShooting = canvas.getAttribute('data-shooting') !== 'false';
    var styles = getComputedStyle(document.documentElement);

    var tintA = hexToRgb(canvas.getAttribute('data-tint-a') || styles.getPropertyValue('--cosmos-a') || '#8B5CF6');
    var tintB = hexToRgb(canvas.getAttribute('data-tint-b') || styles.getPropertyValue('--cosmos-b') || '#3B82F6');
    var tintC = hexToRgb(canvas.getAttribute('data-tint-c') || styles.getPropertyValue('--cosmos-c') || '#E8C57A');

    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var w = 0, h = 0;
    var stars = [];
    var dust = [];
    var shots = [];
    var raf = null;
    var visible = true;
    var t = 0;
    var mx = 0, my = 0, tmx = 0, tmy = 0;

    function rand(a, b) { return a + Math.random() * (b - a); }
    function pick(arr) { return arr[(Math.random() * arr.length) | 0]; }

    function build() {
      var rect = canvas.getBoundingClientRect();
      w = Math.max(rect.width, 1);
      h = Math.max(rect.height, 1);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      /* Scale the count to the visible area so phones do far less work */
      var area = w * h;
      var starCount = Math.round(Math.min(area / 5200, 340) * density);
      var dustCount = Math.round(Math.min(area / 26000, 70) * density);

      stars = [];
      for (var i = 0; i < starCount; i++) {
        var layer = Math.random();
        var depth = layer < 0.6 ? 1 : (layer < 0.88 ? 2 : 3);
        stars.push({
          x: Math.random() * w,
          y: Math.random() * h,
          r: depth === 3 ? rand(1.0, 1.9) : depth === 2 ? rand(0.65, 1.2) : rand(0.3, 0.75),
          d: depth,
          a: rand(0.22, 0.92),
          tw: rand(0.4, 1.9),
          ph: rand(0, Math.PI * 2),
          c: Math.random() < 0.1 ? tintC : (Math.random() < 0.25 ? tintB : null)
        });
      }

      dust = [];
      for (var j = 0; j < dustCount; j++) {
        dust.push({
          x: Math.random() * w,
          y: Math.random() * h,
          r: rand(14, 58),
          a: rand(0.012, 0.05),
          vx: rand(-0.055, 0.055),
          vy: rand(-0.04, 0.02),
          c: pick([tintA, tintB, tintC])
        });
      }
    }

    function drawStar(s) {
      var alpha = reduced ? s.a : s.a * (0.62 + 0.38 * Math.sin(t * s.tw + s.ph));
      var px = s.x + mx * s.d * 9;
      var py = s.y + my * s.d * 7;
      ctx.beginPath();
      ctx.arc(px, py, s.r, 0, Math.PI * 2);
      if (s.c) {
        ctx.fillStyle = 'rgba(' + s.c[0] + ',' + s.c[1] + ',' + s.c[2] + ',' + alpha.toFixed(3) + ')';
      } else {
        ctx.fillStyle = 'rgba(255,255,255,' + alpha.toFixed(3) + ')';
      }
      ctx.fill();

      /* Bloom on the closest layer only — cheap, and reads as depth */
      if (s.d === 3) {
        var g = ctx.createRadialGradient(px, py, 0, px, py, s.r * 7);
        g.addColorStop(0, 'rgba(255,255,255,' + (alpha * 0.30).toFixed(3) + ')');
        g.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(px, py, s.r * 7, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    function drawDust(p) {
      var g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r);
      g.addColorStop(0, 'rgba(' + p.c[0] + ',' + p.c[1] + ',' + p.c[2] + ',' + p.a + ')');
      g.addColorStop(1, 'rgba(' + p.c[0] + ',' + p.c[1] + ',' + p.c[2] + ',0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fill();
    }

    function spawnShot() {
      shots.push({
        x: rand(w * 0.1, w * 0.95),
        y: rand(-40, h * 0.42),
        len: rand(90, 210),
        sp: rand(7, 13),
        a: 1,
        ang: rand(0.32, 0.55)
      });
    }

    function drawShots() {
      for (var i = shots.length - 1; i >= 0; i--) {
        var s = shots[i];
        var dx = Math.cos(s.ang) * s.len;
        var dy = Math.sin(s.ang) * s.len;
        var g = ctx.createLinearGradient(s.x, s.y, s.x - dx, s.y - dy);
        g.addColorStop(0, 'rgba(255,255,255,' + (s.a * 0.95).toFixed(3) + ')');
        g.addColorStop(0.4, 'rgba(' + tintC[0] + ',' + tintC[1] + ',' + tintC[2] + ',' + (s.a * 0.4).toFixed(3) + ')');
        g.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.strokeStyle = g;
        ctx.lineWidth = 1.6;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(s.x - dx, s.y - dy);
        ctx.stroke();

        s.x += Math.cos(s.ang) * s.sp;
        s.y += Math.sin(s.ang) * s.sp;
        s.a -= 0.012;
        if (s.a <= 0 || s.x > w + 260 || s.y > h + 260) shots.splice(i, 1);
      }
    }

    function paint() {
      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'lighter';
      for (var i = 0; i < dust.length; i++) drawDust(dust[i]);
      ctx.globalCompositeOperation = 'source-over';
      for (var j = 0; j < stars.length; j++) drawStar(stars[j]);
      if (allowShooting && shots.length) {
        ctx.globalCompositeOperation = 'lighter';
        drawShots();
        ctx.globalCompositeOperation = 'source-over';
      }
    }

    function loop() {
      t += 0.016;
      mx += (tmx - mx) * 0.045;
      my += (tmy - my) * 0.045;

      for (var i = 0; i < dust.length; i++) {
        var p = dust[i];
        p.x += p.vx; p.y += p.vy;
        if (p.x < -p.r) p.x = w + p.r;
        if (p.x > w + p.r) p.x = -p.r;
        if (p.y < -p.r) p.y = h + p.r;
        if (p.y > h + p.r) p.y = -p.r;
      }

      if (allowShooting && Math.random() < 0.0028 && shots.length < 2) spawnShot();

      paint();
      raf = requestAnimationFrame(loop);
    }

    function start() {
      if (reduced) { paint(); return; }
      if (raf === null) raf = requestAnimationFrame(loop);
    }
    function stop() {
      if (raf !== null) { cancelAnimationFrame(raf); raf = null; }
    }

    /* Only animate while on screen */
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        if (visible) start(); else stop();
      }, { threshold: 0.01 }).observe(canvas);
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop();
      else if (visible) start();
    });

    if (!reduced && !window.matchMedia('(pointer: coarse)').matches) {
      window.addEventListener('pointermove', function (e) {
        var r = canvas.getBoundingClientRect();
        tmx = ((e.clientX - r.left) / r.width) * 2 - 1;
        tmy = ((e.clientY - r.top) / r.height) * 2 - 1;
      }, { passive: true });
    }

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { build(); paint(); }, 180);
    }, { passive: true });

    build();
    start();
  }

  function boot() {
    document.querySelectorAll('canvas[data-cosmos]').forEach(function (c) {
      if (c.dataset.cosmosReady) return;
      c.dataset.cosmosReady = '1';
      Cosmos(c);
    });
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', boot);
})();
